import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from "react-i18next";

import { Table, TableBody, TableHead, TableCell, TableContainer, TableRow, Box, Tab } from '@mui/material';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';

import { getEquipmentOpLogsTimeline, getLiftOpLogsQuery } from 'components/axios/equipmentAxios';

import PageInfo from 'components/PageInfo';
import MainCard from 'components/MainCard';
import EquipmentAlertListCard from './mainCard/EquipmentAlertListCard';
import { getLogValueDescByCodeAndValue } from 'data/data';
import { fromServerFormat } from 'components/date_picker/dateUtils';
import { useEnumApi } from 'contexts/EnumApiContext';
import { useQuery } from '@tanstack/react-query';
import { getEquipmentAlertsDiagnosisByQuery } from 'components/axios/equipmentAlertsAxios';

const EquipmentPanel = ({ equipment, target = 'default' }) => {
  const { t } = useTranslation();
  const { getEnumValueByEnumNameAndCode } = useEnumApi();

  // 🌟 [핵심 1] 페이지 진입 시 0번 탭(또는 1번 탭)으로 즉시 초기화하여 선택된 상태로 시작
  const [pnTab, setPnTab] = useState(target === 'default' ? '0' : '1');
  const [controlPage, setControlPage] = useState(0);
  const [timelinePage, setTimelinePage] = useState(0);
  const [pageSize] = useState(15);

  const handlePaging = (value) => {
    const page = isNaN(value) ? 0 : value - 1;
    if (pnTab === '1') setControlPage(page);
    else if (pnTab === '2') setTimelinePage(page);
  };

  // 0번 탭 데이터 존재 여부 체크
  const { data: alertCheckData, isFetched: isAlertChecked } = useQuery({
    queryKey: ['alertDatas', 'check', equipment?.id, { page: 0, size: 1 }],
    queryFn: () => getEquipmentAlertsDiagnosisByQuery({ page: 0, size: 1, "equipment.id": equipment?.id }),
    enabled: !!equipment?.id && target === 'default',
    staleTime: 1000 * 30,
    gcTime: 1000 * 60 * 5,
  });

  // 1번 탭 데이터 존재 여부 체크
  const { data: controlCheckData, isFetched: isControlChecked } = useQuery({
    queryKey: ['liftOpControlLogs', 'check', equipment?.id, { page: 0, size: 1 }],
    queryFn: () => getLiftOpLogsQuery({ page: 0, size: 1, "equipmentId": equipment?.id }),
    enabled: !!equipment?.id,
    staleTime: 1000 * 30,
    gcTime: 1000 * 60 * 5,
  });

  // 2번 탭 데이터 존재 여부 체크
  const { data: timelineCheckData, isFetched: isTimelineChecked } = useQuery({
    queryKey: ['liftOperationLogs', 'check', equipment?.id, { page: 0, size: 1 }],
    queryFn: () => getEquipmentOpLogsTimeline(equipment.id, { page: 0, size: 1 }),
    enabled: !!equipment?.id,
    staleTime: 1000 * 30,
    gcTime: 1000 * 60 * 5,
  });

  // 데이터 유무 판단
  const hasAlertData = (alertCheckData?.page?.totalElements ?? 0) > 0;
  const hasControlData = (controlCheckData?.totalElements ?? 0) > 0;
  const hasTimelineData = (timelineCheckData?.page?.totalElements ?? 0) > 0;

  // 🌟 [핵심 2] 0번 탭을 먼저 연 상태에서 체크 결과에 따른 예외 자동 보정
  useEffect(() => {
    // 0번 탭으로 열렸는데 0번 탭 데이터 체크가 끝났고, 데이터가 없는 경우만 탭 전환 보정
    if (pnTab === '0' && isAlertChecked && !hasAlertData) {
      if (isControlChecked && hasControlData) {
        setPnTab('1');
      } else if (isTimelineChecked && hasTimelineData) {
        setPnTab('2');
      }
    }
  }, [pnTab, isAlertChecked, hasAlertData, isControlChecked, hasControlData, isTimelineChecked, hasTimelineData]);

  // 1번 탭 (Control Logs) Query
  const { data: controlLogData, isLoading: isControlLoading } = useQuery({
    queryKey: ['liftOpControlLogs', equipment?.id, controlPage, pageSize],
    queryFn: () => getLiftOpLogsQuery({ 
      page: controlPage, 
      size: pageSize, 
      "equipmentId": equipment?.id, 
    }),
    enabled: !!equipment?.id && pnTab === '1', 
    staleTime: 1000 * 10,
    refetchInterval: pnTab === '1' ? 3000 : false, // 🌟 1번 탭이 열려있을 때 3초마다 자동 감지 및 갱신
    placeholderData: (previousData) => previousData, // 🌟 깜빡임 방지 (이전 데이터 유지)
  });

  // 2번 탭 (Timeline Logs) Query
  const { data: timelineLogData, isLoading: isTimelineLoading } = useQuery({
    queryKey: ['liftOperationLogs', equipment?.id, timelinePage, pageSize],
    queryFn: () => getEquipmentOpLogsTimeline(
      equipment.id, 
      { page: timelinePage, size: pageSize }
    ),
    enabled: !!equipment?.id && pnTab === '2',
    staleTime: 1000 * 10,
    refetchInterval: pnTab === '2' ? 3000 : false, // 🌟 2번 탭이 열려있을 때 3초마다 자동 감지 및 갱신
    placeholderData: (previousData) => previousData, // 🌟 깜빡임 방지 (이전 데이터 유지)
  });

  const liftOpControlLogs = controlLogData?.content ?? [];
  const liftOperationLogs = timelineLogData?.content ?? [];

  const pageInfo = useMemo(() => {
    if (pnTab === '1' && controlLogData) {
      return {
        size: controlLogData.size ?? 0,
        totalElements: controlLogData.totalElements ?? 0,
        totalPages: controlLogData.totalPages ?? 0,
        number: controlLogData.number ?? 0
      };
    } 
    if (pnTab === '2' && timelineLogData?.page) {
      return {
        size: timelineLogData.page.size ?? 0,
        totalElements: timelineLogData.page.totalElements ?? 0,
        totalPages: timelineLogData.page.totalPages ?? 0,
        number: timelineLogData.number ?? 0
      };
    }
    return { size: pageSize, totalElements: 0, totalPages: 0, number: 0 };
  }, [pnTab, controlLogData, timelineLogData, pageSize]);

  const selectPanel = (event, newValue) => {
    setPnTab(newValue);
  };

  return (
    <div className='alert_phrase' style={{ padding: "0 10px 10px 10px" }}>
      <Box sx={{ width: '100%', typography: 'body1' }} className="control_panel control_panel1">
        <TabContext value={pnTab}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <TabList onChange={selectPanel}>
              {/* 0번 탭: target이 default이면 일단 보여주다가 체크 후 없으면 제거됨 */}
              {(target === 'default' && (!isAlertChecked || hasAlertData)) && (
                <Tab className="graphTab" label={t('menu.cmms.equipment-alert-history-tracking')} value="0" style={{ display: "inline-block", padding: "6px 15px", margin: "0 5px 7px 0px", borderRadius: "100px", border: `${pnTab === "0" ? "3px solid #0061d0" : "1px solid #0061d0"}` }} />
              )}
              {/* 1, 2번 탭: 백그라운드 체크 완료되어 데이터가 확인되는 대로 표시 */}
              {hasControlData && (
                <Tab className="graphTab" label={t('message.common.title.control.panel.status')} value="1" style={{ display: "inline-block", padding: "6px 15px", margin: "0 5px 7px 0px", borderRadius: "100px", border: `${pnTab === "1" ? "3px solid #0061d0" : "1px solid #0061d0"}` }} />
              )}
              {hasTimelineData && (
                <Tab className="graphTab" label={t('message.common.title.operation.status.history')} value="2" style={{ display: "inline-block", padding: "6px 15px", margin: "0 5px 7px 0px", borderRadius: "100px", border: `${pnTab === "2" ? "3px solid #0061d0" : "1px solid #0061d0"}` }} />
              )}
            </TabList>
          </Box>
          <MainCard className="center_table" content={false}>
            
            {/* 0번 탭 Panel */}
            {target === 'default' && (
              <TabPanel value="0">
                <EquipmentAlertListCard equipmentId={equipment?.id} equipmentNo={equipment?.equipmentNo} type="tab" />
              </TabPanel>
            )}

            {/* 1번 탭 Panel */}
            <TabPanel value="1" style={{ display: pnTab === '1' ? "flex" : "none", flexDirection: "column" }}>
              {isControlLoading && liftOpControlLogs.length === 0 ? (
                <div>Loading...</div>
              ) : (
                <div className='cn_dash_tabbox liftControlPanel'>
                  <TableContainer>
                    <Table sx={{ minWidth: 600 }} className='same_width'>
                      <TableHead>
                        <TableRow>
                          <TableCell>{t('message.common.subtitle.diagnosis.datetime')}</TableCell>
                          <TableCell>{t('message.common.subtitle.companyType')}</TableCell>
                          <TableCell>{t('message.common.subtitle.description')}</TableCell>
                          <TableCell>{t('message.common.subtitle.component.position')}-{t('message.common.subtitle.material')}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {liftOpControlLogs.map((log, k) => {
                          const getData = () => {
                            switch (log.type) {
                              case 'DRV':
                                return {
                                  desc: log.drv?.status === 'ERROR' ? getEnumValueByEnumNameAndCode('IntegratedDiagnosisLogStatus', log.drv?.status)?.[0]?.title 
                                      : (
                                          (log.drv?.lowestGrade ? `${t('message.common.text.lowestVibrationGrade')} : ${log.drv?.lowestGrade}\n` : '')
                                          +(log.drv?.shaftDiagnosisCodes?.length > 0 ? `${t('message.common.subtitle.axis')} : ${log.drv?.shaftDiagnosisCodes?.map(d => d.subCategoryNm + "(" + d.categoryNm + ")").join(',')}\n` : '')
                                          +(log.drv?.horizonDiagnosisCodes?.length > 0 ? `${t('message.common.subtitle.radius')} : ${log.drv?.horizonDiagnosisCodes?.map(d => d.subCategoryNm + "(" + d.categoryNm + ")").join(',')}\n` : '')
                                          +(log.drv?.verticalDiagnosisCodes?.length > 0 ? `${t('message.common.subtitle.vertical')} : ${log.drv?.verticalDiagnosisCodes?.map(d => d.subCategoryNm + "(" + d.categoryNm + ")").join(',')}` : '')
                                        ),
                                  code: log.drv?.errorCode,
                                  status: getEnumValueByEnumNameAndCode('IntegratedDiagnosisLogStatus', log.drv?.status)?.[0]?.title,
                                };
                              case 'DRC':
                                return {
                                  desc: log.drc?.status === 'ERROR' ? getEnumValueByEnumNameAndCode('IntegratedDiagnosisLogStatus', log.drc?.status)?.[0]?.title 
                                      : `${t('message.common.text.chainTwistCheck')} : ` + (log.drc?.twistedYn ?? ''),
                                  code: log.drc?.errorCode,
                                  status: getEnumValueByEnumNameAndCode('IntegratedDiagnosisLogStatus', log.drc?.status)?.[0]?.title,
                                };
                              case 'DRB':
                                return {
                                  desc: log.drb?.status === 'ERROR' ? getEnumValueByEnumNameAndCode('IntegratedDiagnosisLogStatus', log.drb?.status)?.[0]?.title 
                                      : `${t('message.common.text.vBeltSagCheck')} : ` + (log.drb?.saggingYn ?? ''),
                                  code: log.drb?.errorCode,
                                  status: getEnumValueByEnumNameAndCode('IntegratedDiagnosisLogStatus', log.drb?.status)?.[0]?.title,
                                };
                              case 'DRN':
                                return {
                                  desc: log.drn?.status === 'ERROR' ? getEnumValueByEnumNameAndCode('IntegratedDiagnosisLogStatus', log.drn?.status)?.[0]?.title 
                                      : `${t('message.common.text.noiseAbnormalCheck')} : ` + (log.drn?.abnormalCheck ?? ''),
                                  code: log.drn?.errorCode,
                                  status: getEnumValueByEnumNameAndCode('IntegratedDiagnosisLogStatus', log.drn?.status)?.[0]?.title,
                                };
                              case 'ETL':
                                return {
                                  desc: log.etl?.status === 'ERROR' ? getEnumValueByEnumNameAndCode('IntegratedDiagnosisLogStatus', log.etl?.status)?.[0]?.title 
                                      : `${t('message.common.text.equipmentTemperature')} : ` + log.etl?.temperature,
                                  code: log.etl?.error,
                                  status: getEnumValueByEnumNameAndCode('IntegratedDiagnosisLogStatus', log.etl?.status)?.[0]?.title,
                                };
                              default:
                                return { desc: null, code: null, status: null };
                            }
                          };

                          return (
                            <TableRow key={log.id}>
                              <TableCell>
                                {/* {k===0 && console.log('-->createdAt:', log?.id, log?.createdAt)} */}
                                {fromServerFormat(log?.createdAt, { format: t('message.common.text.date.format.date') })}<br />
                                {fromServerFormat(log?.createdAt, { format: t('message.common.text.date.format.time') })}
                              </TableCell>
                              <TableCell>{getEnumValueByEnumNameAndCode('DiagnosisResultType', log?.type)?.[0]?.title}</TableCell>
                              <TableCell style={{ whiteSpace: 'pre-line', textAlign: 'left' }}>{getData()?.desc}</TableCell>
                              <TableCell>{log.equipmentPart?.componentLocation?.name}-{log.equipmentPart?.masterEquipmentPart?.name}</TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <PageInfo pageInfo={pageInfo} handlePaging={handlePaging} />
                </div>
              )}
            </TabPanel>

            {/* 2번 탭 Panel */}
            <TabPanel value="2" style={{ display: pnTab === '2' ? "flex" : "none", flexDirection: "column" }}>
              {isTimelineLoading && liftOperationLogs.length === 0 ? (
                <div>Loading...</div>
              ) : (
                <div className='cn_dash_tabbox'>
                  <TableContainer>
                    <Table sx={{ minWidth: 600 }} className='same_width'>
                      <TableHead>
                        <TableRow>
                          <TableCell>{t('message.common.subtitle.dateAt')}</TableCell>
                          <TableCell>{t('message.common.subtitle.mode')}</TableCell>
                          <TableCell>{t('message.common.subtitle.status')}</TableCell>
                          {equipment?.controlPanel?.usedCurrentFloor && <TableCell>Floor</TableCell>}
                          {equipment?.controlPanel?.usedDoorStatus && <TableCell>DoorStatus</TableCell>}
                          <TableCell>{t('message.common.subtitle.component.location')}-{t('message.common.subtitle.material')}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {liftOperationLogs.map((log, index) => (
                          <TableRow key={log.id ?? index} className='pop_tr'>
                            <TableCell>{fromServerFormat(log?.eventTime, { format: t('message.common.text.date.format.datetimess') })}</TableCell>
                            <TableCell>{log.modeName ?? ''}</TableCell>
                            <TableCell>
                              {log.logType === 'OP_LOG' ? 
                                getEnumValueByEnumNameAndCode('OperatingStatus', log.statusValue)?.[0]?.title 
                                : (log.measureCode === 'ST' ? 
                                  t(getLogValueDescByCodeAndValue('text', log.measureCode, log.statusValue, log.partMasterCode, log.locationCode).value) 
                                  : (log.measureValueName || log.statusValue))
                              }
                            </TableCell>
                            {equipment?.controlPanel?.usedCurrentFloor && <TableCell>{log.currentFloor}</TableCell>}
                            {equipment?.controlPanel?.usedDoorStatus && <TableCell>{getEnumValueByEnumNameAndCode('DoorStatus', log.doorStatus)?.[0]?.title}</TableCell>}
                            <TableCell>{log.logType === 'MEASURE' ? log.locationName + '-' + log.partMasterName : ''}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <PageInfo pageInfo={pageInfo} handlePaging={handlePaging} />
                </div>
              )}
            </TabPanel>

          </MainCard>
        </TabContext>
      </Box>
    </div>
  );
};

export default EquipmentPanel;